import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Lightning, TrendUp, TrendDown, CaretRight, X } from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";
import { AdminShell } from "@/components/AdminShell";
import { t } from "@/lib/i18n";

const STATUS_STYLE = {
  healthy: "bg-emerald-50 text-emerald-700",
  watch: "bg-amber-50 text-amber-700",
  struggling: "bg-red-50 text-red-700",
  learning: "bg-blue-50 text-blue-700",
  low_volume: "bg-gray-100 text-gray-500",
};

export default function AdminPriorityPage() {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailUser, setDetailUser] = useState(null);

  useEffect(() => {
    api.get("/admin/priority/overview").then((r) => setData(r.data)).catch((e) => toast.error(errDetail(e)));
  }, []);

  const openDetail = async (row) => {
    setDetailUser(row);
    try {
      const { data: d } = await api.get(`/admin/priority/user/${row.user_id}`);
      setDetail(d);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const statusLabel = (s) => ({
    healthy: t("pi.ad_healthy", "healthy"), watch: t("pi.ad_watch", "watch"),
    struggling: t("pi.ad_struggling", "struggling"), learning: t("pi.ad_learning", "learning"),
    low_volume: t("pi.ad_low_volume", "low volume"),
  }[s] || s);

  return (
    <AdminShell title={t("pi.ad_title", "Priority Inbox — Analytics")}>
      <div data-testid="admin-priority-page">
        {data && (
          <div data-testid="admin-priority-summary" className="flex flex-wrap gap-6 border border-gray-200 rounded-md px-5 py-3.5 mb-5 text-sm">
            <span><b data-testid="ap-users-active">{data.summary.users_active}</b> {t("pi.ad_users", "users active")}</span>
            <span>{t("pi.ad_avg", "avg")} <b data-testid="ap-avg-pct">{data.summary.avg_pct !== null ? `${data.summary.avg_pct}%` : "—"}</b></span>
            <span><b data-testid="ap-passing">{data.summary.passing}</b> ≥85% ✅</span>
            <span><b data-testid="ap-struggling">{data.summary.struggling}</b> &lt;70% ⚠</span>
            <span data-testid="ap-avg-tokens">{t("pi.ad_avg_tok", "Ø tokens/email")} <b>{data.summary.avg_tokens_per_email ?? 0}</b></span>
          </div>
        )}
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-4 py-2.5">{t("pi.ad_user", "User")}</th>
                <th className="px-4 py-2.5">{t("pi.ad_package", "Package")}</th>
                <th className="px-4 py-2.5">{t("pi.ad_thiswk", "This wk")}</th>
                <th className="px-4 py-2.5">{t("pi.ad_vslast", "vs last")}</th>
                <th className="px-4 py-2.5">{t("pi.ad_weeks", "Weeks")}</th>
                <th className="px-4 py-2.5">{t("pi.ad_tokens", "Tokens")}</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((r) => (
                <tr key={r.user_id} data-testid={`ap-row-${r.user_id}`} onClick={() => openDetail(r)}
                  className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer">
                  <td className="px-4 py-2.5 font-medium text-gray-800">{r.email}</td>
                  <td className="px-4 py-2.5 text-gray-500 capitalize">{r.package}</td>
                  <td className="px-4 py-2.5">
                    {r.status === "learning" ? (
                      <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${STATUS_STYLE.learning}`}>{t("pi.ad_learning_wk", "learning (wk {n} of 3)", { n: r.weeks_active })}</span>
                    ) : r.status === "low_volume" ? (
                      <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${STATUS_STYLE.low_volume}`}>{t("pi.ad_low_actions", "low volume ({n} actions)", { n: r.sample })}</span>
                    ) : (
                      <span data-testid={`ap-pct-${r.user_id}`} className={`text-xs font-semibold rounded-full px-2 py-0.5 ${STATUS_STYLE[r.status]}`}>
                        {r.latest_pct}% {r.status === "healthy" ? "✅" : r.status === "struggling" ? "⚠" : ""}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {r.delta !== null && r.delta !== undefined && (
                      <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${r.delta >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {r.delta >= 0 ? <TrendUp size={12} /> : <TrendDown size={12} />}{r.delta >= 0 ? "+" : ""}{r.delta}%
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500">{r.weeks_active}</td>
                  <td className="px-4 py-2.5 text-gray-500">{r.tokens_spent >= 1000 ? `${(r.tokens_spent / 1000).toFixed(1)}k` : r.tokens_spent}<span className="text-gray-300"> · Ø{r.avg_tokens ?? 0}</span></td>
                  <td className="px-4 py-2.5 text-gray-300"><CaretRight size={13} /></td>
                </tr>
              ))}
              {data && data.rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400" data-testid="ap-empty">
                  {t("pi.ad_empty", "No users with Priority Inbox yet")}</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {detailUser && (
          <div className="fixed inset-0 z-40 bg-black/30" onClick={() => { setDetailUser(null); setDetail(null); }}>
            <div data-testid="ap-drilldown" onClick={(e) => e.stopPropagation()}
              className="absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl overflow-y-auto p-6">
              <div className="flex items-start justify-between mb-4">
                <h2 className="font-heading font-bold text-gray-900 flex items-center gap-2">
                  <Lightning size={16} className="text-amber-500" weight="fill" /> {detailUser.email}
                </h2>
                <button data-testid="ap-drilldown-close" onClick={() => { setDetailUser(null); setDetail(null); }}
                  className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
              </div>
              {detail && (<>
                <div data-testid="ap-week-bars" className="border border-gray-200 rounded-md p-4 mb-4">
                  <p className="text-xs font-semibold text-gray-500 mb-2">{t("pi.ad_12w", "Weekly agreement")}</p>
                  <div className="flex items-end gap-1.5 h-20">
                    {detail.weeks.map((w, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${w.week_start_local}: ${w.agreement_pct ?? "—"}%`}>
                        <div className={`w-full rounded-t ${w.agreement_pct === null || w.agreement_pct === undefined ? "bg-gray-200" : w.agreement_pct >= 85 ? "bg-emerald-500" : w.agreement_pct >= 70 ? "bg-amber-400" : "bg-red-400"}`}
                          style={{ height: `${Math.max(6, (w.agreement_pct || 0) * 0.7)}px` }} />
                        <span className="text-[9px] text-gray-400">{w.agreement_pct ?? "·"}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {detail.weeks.slice().reverse().map((w, i) => (
                  <div key={i} data-testid={`ap-week-${i}`} className="border border-gray-100 rounded-md p-3 mb-2 text-xs">
                    <p className="font-semibold text-gray-700">
                      {w.week_start_local} — {w.agreement_pct !== null ? `${w.agreement_pct}%` : t("pi.ad_low_volume", "low volume")}
                      {w.delta_vs_prev !== null && w.delta_vs_prev !== undefined && (
                        <span className={w.delta_vs_prev >= 0 ? "text-emerald-600" : "text-red-600"}> ({w.delta_vs_prev >= 0 ? "▲+" : "▼"}{w.delta_vs_prev})</span>
                      )} · {w.sample} {t("pi.rp_actions", "actions")} · {w.tz}
                    </p>
                    {(w.why_improved || []).map((s, j) => <p key={j} className="text-emerald-700 mt-0.5">✅ {s}</p>)}
                    {(w.why_degraded || []).map((s, j) => <p key={j} className="text-red-600 mt-0.5">⚠ {s}</p>)}
                    {(w.applied || []).map((a, j) => <p key={j} className="text-gray-600 mt-0.5">✅ {t("pi.ad_applied", "applied")}: {JSON.stringify(a.changes)}</p>)}
                    {(w.rejected || []).map((rj, j) => <p key={j} className="text-gray-500 mt-0.5">✖ {t("pi.ad_rejected", "rejected")}: {JSON.stringify(rj.changes)} — {rj.reason}</p>)}
                    {Object.keys(w.miss_categories || {}).length > 0 && (
                      <p className="text-gray-400 mt-0.5">{t("pi.ad_missbreak", "Miss breakdown")}: {Object.entries(w.miss_categories).map(([k, v]) => `${k} ${v}`).join(" · ")}</p>
                    )}
                  </div>
                ))}
                <p className="text-[11px] text-gray-400 mt-2">
                  {detail.taught_exceptions} {t("pi.ad_taught", "taught exceptions")} · {detail.pending_proposals.length} {t("pi.ad_pending", "pending proposals")} · {detail.calibration_n} {t("pi.ad_comparisons", "calibration comparisons")}
                </p>
              </>)}
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
